<script setup lang="ts">
import { onBeforeUnmount, ref, watch, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ResultBottomSheet } from '@fire-calc/ui-vue';

interface ComparisonRow {
  label: string;
  classicalValue: string;
  dwzValue: string;
}

interface Props {
  open: boolean;
  header: string;
  classicalLabel: string;
  dwzLabel: string;
  rows: ReadonlyArray<ComparisonRow>;
}

const props = defineProps<Props>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();

function onScrimClick(): void {
  emit('close');
}

function onCloseClick(): void {
  emit('close');
}

// TODO: focus trap (out of scope for v1).
const escListenerInstalled: Ref<boolean> = ref(false);

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    emit('close');
  }
}

watch(
  () => props.open,
  (open) => {
    if (open && !escListenerInstalled.value) {
      document.addEventListener('keydown', onKeyDown);
      escListenerInstalled.value = true;
    } else if (!open && escListenerInstalled.value) {
      document.removeEventListener('keydown', onKeyDown);
      escListenerInstalled.value = false;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (escListenerInstalled.value) {
    document.removeEventListener('keydown', onKeyDown);
    escListenerInstalled.value = false;
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-40"
      data-testid="bottom-sheet-root"
    >
      <div
        data-testid="bottom-sheet-scrim"
        class="absolute inset-0 bg-text-primary/40"
        @click="onScrimClick"
      />
      <div
        data-testid="bottom-sheet-panel"
        class="absolute bottom-0 left-0 right-0 mx-auto max-w-floating-bar bg-bg-page rounded-t-large border-t border-border-light shadow-floating-bar"
      >
        <div class="flex justify-end px-md pt-sm">
          <button
            type="button"
            data-testid="bottom-sheet-close"
            class="text-text-tertiary text-title-large"
            :aria-label="t('bottomSheetCloseLabel')"
            @click="onCloseClick"
          >&times;</button>
        </div>
        <div class="px-lg pb-lg">
          <ResultBottomSheet
            :header="header"
            :classical-label="classicalLabel"
            :dwz-label="dwzLabel"
            :rows="[...rows]"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
